<script lang="ts">
  import { api } from '$utils/api';
  import { createQuery } from '@tanstack/svelte-query';
  import LocSearchResult from './Loc_SearchResult.svelte';
  import type { GlobalSearchResult, SearchType } from 'garden-common';
  import { createEventDispatcher, onDestroy, onMount, tick } from 'svelte';
  import { debounce } from 'lodash-es';
  import { clickOutside } from '$src/utils/clickOutside.util';
  import SearchBar from '$lib/components/homepage/SearchBar.svelte';

  import KeyboardHint from '$icons/KeyboardHintIcon.svelte';
  import Loader from '$icons/LoaderIcon.svelte';
  import { isMobile } from '$stores/media.store';
  import Button from '$lib/components/common/buttons/Button.svelte';
  import UserCard from '$lib/components/common/user/UserCard.svelte';
  import ExplorePost from '$lib/components/common/ExplorePost.svelte';
  import GalleryCard from '$lib/components/gallery/GalleryCard.svelte';

  const dispatch = createEventDispatcher<{
    close: {};
    showAllResults: { searchTerm: string };
  }>();

  export let searchTerm = '';
  export let searchType: SearchType = 'all';

  const tabs: { title: string; value: SearchType }[] = [
    { title: 'All', value: 'all' },
    { title: 'Posts', value: 'posts' },
    { title: 'People', value: 'users' },
    { title: 'Galleries', value: 'galleries' }
  ];

  let selectedTab = tabs.find(i => i.value === searchType) || $isMobile ? tabs[1] : tabs[0];

  let loading = false;
  let nResults = 0;

  const searchQuery = createQuery({
    queryKey: ['homepageSearch'],
    queryFn: async () => {
      if (!selectedTab.value || searchTerm === '') {
        return [];
      }
      const res = await api.get(`/v2/feed/search/${searchType}/${searchTerm}`);
      return res.data.results;
    },
    initialData: []
  });

  let focusedSearchResult: GlobalSearchResult;

  async function search() {
    const res = await $searchQuery.refetch();

    focusedSearchResult = res.data[0];

    nResults = res.data.length;
  }

  const debouncedSearch = debounce(search, 500);

  $: searchTerm, debouncedSearch();

  function onTabClick(tab: (typeof tabs)[number]) {
    selectedTab = tab;
    searchType = tab.value;
    search();
  }

  function escHandler(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      dispatch('close');
    }
  }

  onMount(async () => {
    document.addEventListener('keydown', escHandler);

    await tick();

    requestAnimationFrame(() => {
      input.focus();

      document.addEventListener(
        'mouseup',
        () => {
          try {
            (document.activeElement as HTMLElement).blur();
          } catch (e) {}

          requestAnimationFrame(() => {
            input?.focus();
          });
        },
        { once: true }
      );
    });
  });

  onDestroy(() => {
    document.removeEventListener('keydown', escHandler);
  });

  let input: HTMLInputElement;
</script>

<div
  class="{$isMobile
    ? 'w-full h-full bg-white pt-10'
    : 'w-4/5 inset-x-0 absolute mx-auto top-4 flex flex-col'} "
  use:clickOutside
  on:click_outside={() => dispatch('close')}
>
  <div class="{$isMobile ? 'flex items-center gap-2' : ''} w-full px-2">
    {#if $isMobile}
      <button
        on:click={() => {
          dispatch('close');
        }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.57 5.93018L3.5 12.0002L9.57 18.0702"
            stroke="#1B1B1B"
            stroke-width="2"
            stroke-miterlimit="10"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M20.5 12H3.66998"
            stroke="#1B1B1B"
            stroke-width="2"
            stroke-miterlimit="10"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    {/if}
    <SearchBar
      bind:typingSearch={searchTerm}
      bind:inputHTML={input}
      bind:loading
      selected={true}
      searchIcon={false}
      on:focus
      enteredValue={search}
      dropDown={false}
      placeholder="Search"
    >
      <span slot="right-slot" class="mr-1 {!$isMobile ? 'flex h-9 gap-2 px-1' : ''}">
        {#if !$isMobile}
          <Button
            border={false}
            class="w-[170px]"
            primary
            clickOnKey="Enter"
            label="Show all results"
            disabled={!searchTerm}
            on:click={() => dispatch('showAllResults', { searchTerm })}
          >
            <div slot="icon-right">
              <KeyboardHint type="enter" />
            </div>
          </Button>

          <Button
            secondary
            clickOnKey="Escape"
            label="Close"
            on:click={() => {
              dispatch('close');
            }}
          >
            <div slot="icon-right">
              <KeyboardHint type="escape" />
            </div>
          </Button>
        {/if}</span
      >
    </SearchBar>

    <!-- <button>Show all results </button>

		<button> Close </button> -->
  </div>

  <div
    class="bg-white rounded-[20px] h-[601px] py-[12px] overflow-hidden {$isMobile ? '' : 'mt-4'}"
  >
    <!-- Search result type selector row -->
    <div class="flex gap-[8px] pl-[12px]">
      {#each tabs as tab}
        {@const isSelected = tab.value === searchType}

        <button
          class="px-[12px] py-[8px] font-medium rounded-[24px] hover:bg-[#e7ecea] transition-all duration-100
				{isSelected ? 'text-brand-green bg-garden-grey-200' : 'text-blueish-grey-500 bg-blueish-grey-100'}"
          on:click={() => onTabClick(tab)}
        >
          {tab.title}
        </button>
      {/each}
    </div>

    <div class="h-px bg-grey-transparency-dark bg-opacity-[0.04] mt-[12px]" />

    {#if $searchQuery.isFetching}
      <div class="w-full h-96 flex items-center justify-center">
        <Loader size={56} />
      </div>
    {:else}
      <div class="flex h-full">
        <div class="flex-grow">
          <div class="mt-[12px] text-garden-grey-400 font-bold pl-[12px]">
            <!-- {$searchQuery.isFetching} -->
            {#if searchTerm.length !== 0}
              {nResults} results for "{searchTerm}"
            {:else}
              Type to see results for that search term
            {/if}
          </div>

          <!-- Search results -->
          <div class="mt-[10px] h-full overflow-auto pb-24">
            {#if $searchQuery && $searchQuery.isSuccess}
              {#each $searchQuery.data as result}
                <LocSearchResult
                  data={result}
                  on:close={() => {
                    dispatch('close');
                  }}
                  on:hover={() => (focusedSearchResult = result)}
                />
              {/each}
            {:else}
              <div class="font-bold px-3 text-garden-grey-400">
                {#if $searchQuery.isLoading}
                  Loading...
                {:else if $searchQuery.error}
                  Error!
                {/if}
              </div>
            {/if}
          </div>
        </div>

        <!-- Focused search result preview -->
        {#key focusedSearchResult}
          {#if $searchQuery && !$isMobile}
            <div class="p-[16px] w-96 overflow-hidden h-fit min-h-[500px]">
              {#if focusedSearchResult?.type === 'user'}
                <UserCard   user={focusedSearchResult} containerCls="!h-full" />
              {/if}

              {#if focusedSearchResult?.type === 'post'}
                <ExplorePost post={focusedSearchResult} hideAuthor containerCls="h-fit" />
              {/if}

              {#if focusedSearchResult?.type === 'gallery'}
                <GalleryCard
                  gallery={focusedSearchResult}
                  variant="vertical"
                  containerCls="!h-full"
                  disableTransitions
                />
              {/if}
            </div>
          {/if}
        {/key}
      </div>
    {/if}
  </div>
</div>
